import * as Sentry from "@sentry/react";
import { Route, Routes, Navigate } from "react-router-dom";

import CaseEditor from "admin/CaseEditor/CaseEditor";
import { useSelectedLabStation } from "hooks/useSelectedLabStation";
import CaseAccession from "pages/accessioning/CaseAccession";
import CaseBarcodes from "pages/accessioning/CaseBarcodes";
import CaseDetail from "pages/accessioning/CaseDetail";
import CaseHistory from "pages/accessioning/CaseHistory";
import BadgePage from "pages/BadgePage";
import BatchPrintPage from "pages/BatchPrintPage";
import NotFoundPage from "pages/NotFoundPage";
import ObjectPrintPage from "pages/ObjectPrintPage";
import PrintPage from "pages/PrintPage";
import SendoutRequisitionPage from "pages/sendout/SendoutRequisitionPage";
import Shipment from "pages/shipping/Shipment";
import Shipments from "pages/shipping/Shipments";
import Shipping from "pages/shipping/Shipping";
import StationsIndexPage from "pages/StationsIndexPage";
import { UserManagement } from "pages/users/UserManagement";
import ZebraPrintPage from "pages/ZebraPrintPage";
import AccessioningStationEdit from "stations/AccessioningStationEdit";
import CuttingStationBlock from "stations/CuttingStationBlock";
import CuttingStationList from "stations/CuttingStationList";
import EmbeddingStationList from "stations/EmbeddingStationList";
import GrossingStationDocument from "stations/GrossingStationDocument";
import GrossingStationList from "stations/GrossingStationList";
import GrossingStationSpecimenGate from "stations/GrossingStationSpecimenGate";
import ScanningStationList from "stations/ScanningStationList";
import StationLayout from "stations/StationLayout";
import StationNew from "stations/StationLayout";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const AppRoutes = () => {
  const selectedLabStation = useSelectedLabStation();

  return (
    <SentryRoutes>
      <Route
        path="/"
        element={
          selectedLabStation ? (
            <Navigate
              to={
                selectedLabStation.type === "accessioning"
                  ? "/cases"
                  : `/station/${selectedLabStation.type}/`
              }
            />
          ) : null
        }
      />

      <Route path="/admin/cases/:case_id" element={<CaseEditor />} />
      <Route path="/cases" element={<StationNew />}>
        <Route path="" element={<CaseAccession />} />
        <Route path=":case_id">
          <Route path="" element={<CaseDetail />} />
          <Route path="history" element={<CaseHistory />} />
          <Route path="barcodes" element={<CaseBarcodes />} />
        </Route>
      </Route>

      {/* Removed from being "nested" as not to inherit HTML, because
          this is a printable page (we don't want the 'Outlet') */}
      <Route
        path="/cases/:case_id/sendout_requisition"
        element={<SendoutRequisitionPage />}
      ></Route>

      <Route path="/users">
        <Route path="" element={<UserManagement />} />
      </Route>

      <Route path="/shipping" element={<Navigate to="/station/shipping" />} />
      <Route path="/station/shipping">
        <Route path="" element={<Shipping />} />
        <Route path="shipments" element={<Shipments />} />
        <Route path="shipments/:shipment_id" element={<Shipment />} />
      </Route>

      <Route path="/station/accessioning">
        <Route path=":accession_id" element={<AccessioningStationEdit />} />
        <Route path="" element={<Navigate to="/cases" />} />
      </Route>

      <Route
        path="/station/grossing"
        element={<StationLayout stationDocumentWrapper />}
      >
        <Route path="" element={<GrossingStationList />} />
        <Route
          path="document/:document_id"
          element={<GrossingStationDocument />}
        />
        <Route
          path="document/:document_id/specimen/:specimen_id"
          element={<GrossingStationSpecimenGate />}
        />
      </Route>

      <Route path="/station/embedding" element={<StationLayout />}>
        <Route path="" element={<EmbeddingStationList />} />
      </Route>

      <Route path="/station/cutting" element={<StationLayout />}>
        <Route path="" element={<CuttingStationList />} />
        <Route path=":block_id" element={<CuttingStationBlock />} />
      </Route>

      <Route path="/station/scanning" element={<StationLayout />}>
        <Route path="" element={<ScanningStationList />} />
      </Route>

      <Route path="/badge" element={<BadgePage />} />
      <Route path="/print/:case_id" element={<PrintPage />} />
      <Route path="/batch_print/:case_ids" element={<BatchPrintPage />} />
      <Route
        path="/print_object/:object/:payload"
        element={<ObjectPrintPage />}
      />
      <Route
        path="/slide/:slide_id/print/:printer"
        element={<ZebraPrintPage />}
      />

      <Route path="" element={<StationsIndexPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </SentryRoutes>
  );
};

export default AppRoutes;
